<template>
  <div class="tournament-skin-settings">
    <div class="section-title-row">
      <v-icon small class="mr-2">fa-regular fa-masks-theater</v-icon>
      <span class="section-title">{{ $t('skinThemeSettings.title') }}</span>
    </div>

    <div class="info-card">
      <div class="config-grid">
        <div class="config-controls">
          <div class="switch-row">
            <div class="switch-label">
              <div class="switch-title">{{ $t('skinThemeSettings.enableTitle') }}</div>
              <div class="switch-desc">{{ $t('skinThemeSettings.enableDescription') }}</div>
            </div>
            <v-switch
              v-model="enabled"
              :disabled="lockedByParticipants"
              color="var(--v-mainGreen-base)"
              hide-details
              inset
            />
          </div>

          <div v-if="lockedByParticipants" class="lock-hint">
            <v-icon small class="mr-1">fa-regular fa-circle-info</v-icon>
            {{ $t('skinThemeSettings.lockedByParticipants') }}
          </div>

          <v-divider class="my-5" />

          <div class="theme-picker">
            <div class="switch-label">
              <div class="switch-title">{{ $t('skinThemeSettings.categoryTitle') }}</div>
              <div class="switch-desc">{{ $t('skinThemeSettings.categoryDescription') }}</div>
            </div>
        <v-select
          v-model="selectedThemeId"
          :items="themes"
          :label="$t('skinThemeSettings.categoryLabel')"
          :placeholder="$t('skinThemeSettings.categoryPlaceholder')"
          :disabled="!enabled || lockedByParticipants"
          :loading="loadingThemes"
          :rules="enabled ? [v => !!v || $t('skinThemeSettings.categoryRequired')] : []"
          item-text="displayName"
          item-value="id"
          outlined
          dense
          class="mt-3 skin-theme-select"
          :menu-props="{ offsetY: true, zIndex: 500, maxHeight: 320 }"
        >
          <template v-slot:selection="{ item }">
            <div class="theme-option">
              <div class="theme-icon-badge">
                <img v-if="themeAssets(item)" :src="themeAssets(item).icon" :alt="themeLabel(item)" class="theme-icon" />
                <span v-else class="theme-icon-fallback">{{ initials(themeLabel(item)) }}</span>
              </div>
              <span>{{ themeLabel(item) }}</span>
            </div>
          </template>
          <template v-slot:item="{ item }">
            <div class="theme-option">
              <div class="theme-icon-badge">
                <img v-if="themeAssets(item)" :src="themeAssets(item).icon" :alt="themeLabel(item)" class="theme-icon" />
                <span v-else class="theme-icon-fallback">{{ initials(themeLabel(item)) }}</span>
              </div>
              <span>{{ themeLabel(item) }}</span>
            </div>
          </template>
        </v-select>
          </div>
        </div>

        <div v-if="enabled && selectedThemeCode && assetsFor(selectedThemeCode)" class="config-preview">
          <div class="preview-label">{{ $t('skinThemeSettings.previewLabel') }}</div>
          <div class="preview-frame">
            <div class="preview-scale">
              <SkinnedTournamentOverviewCard
                :participant-data="previewParticipant"
                :participants="[previewParticipant]"
                :theme-code-override="selectedThemeCode"
                :awards-override="[]"
                :should-show-awards="false"
                :should-show-sponsor="false"
                :preview-mode="true"
                ranking-type="BALANCE"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Repository from '../../../../repositories/tournament';
import { mapMutations } from 'vuex';
import categoryAssets from '@/assets/skins_assets';
import SkinnedTournamentOverviewCard from '@/components/SkinnedTournamentOverviewCard.vue';

// The football theme carries different identifiers per environment (code WC26,
// code FOOTBALL, or display name "Football"); it must always be presented under
// the licensing-safe label.
const FOOTBALL_THEME_LABEL = 'Anonymous Football Players';

export default {
  name: 'SkinThemeTab',
  components: { SkinnedTournamentOverviewCard },
  props: {
    setNextBtnDisabled: { type: Function, required: false },
  },
  data() {
    return {
      enabled: false,
      selectedThemeId: null,
      themes: [],
      loadingThemes: false,
      initialValues: null,
    };
  },
  computed: {
    tournamentSummary() {
      return this.$store.state.tournament;
    },
    participantsCount() {
      return this.tournamentSummary?.participantsCount
        ?? this.tournamentSummary?.participants?.length
        ?? 0;
    },
    lockedByParticipants() {
      return this.participantsCount > 0;
    },
    selectedThemeCode() {
      const theme = this.themes.find((t) => t.id === this.selectedThemeId);
      return theme ? theme.code : null;
    },
    previewBgStyle() {
      const code = this.selectedThemeCode;
      const assets = code ? this.assetsFor(code) : null;
      if (!assets) return {};
      return { backgroundImage: `url(${assets.cardBackground})` };
    },
    previewParticipant() {
      const assets = this.selectedThemeCode ? this.assetsFor(this.selectedThemeCode) : null;
      const sample = this.previewCharacterFor(this.selectedThemeCode);
      return {
        participantId: 'preview',
        userId: 'preview',
        firstName: 'Esteban',
        lastName: 'Prieto',
        username: 'preview',
        status: 'ACTIVE',
        score: 2456,
        place: 4,
        brokerAccountIdentifier: '1090107461',
        brokerName: 'Broker Name',
        skin: {
          name: sample.name,
          firstName: sample.firstName,
          lastName: sample.lastName,
          country: sample.country,
          category: this.selectedThemeCode,
          sourceImage: null,
          imageUrl: assets ? assets.charMock : null,
        },
      };
    },
    previewAwards() {
      return [
        { rank: 1, title: 'FTMO 100K', description: '1st place', estimatedValue: '1000' },
      ];
    },
  },
  mounted() {
    this.loadThemes();
    if (this.tournamentSummary) this.init();
    if (this.setNextBtnDisabled) this.setNextBtnDisabled(true);
  },
  watch: {
    tournamentSummary: {
      handler(v) {
        if (v && !this.initialValues) this.init();
      },
      immediate: true,
    },
    enabled(v) {
      if (!v) this.selectedThemeId = null;
      this.checkForChanges();
    },
    selectedThemeId() {
      this.checkForChanges();
    },
  },
  methods: {
    ...mapMutations(['setTournament']),
    assetsFor(code) {
      if (!code) return null;
      const key = String(code).toUpperCase();
      return categoryAssets[key] || null;
    },
    isFootballTheme(item) {
      const code = String(item?.code || '').toUpperCase();
      const name = String(item?.displayName || '').trim().toUpperCase();
      return code === 'WC26' || code === 'FOOTBALL' || name === 'WC26' || name === 'FOOTBALL';
    },
    themeLabel(item) {
      if (this.isFootballTheme(item)) return FOOTBALL_THEME_LABEL;
      return this.prettifyName(item?.displayName);
    },
    themeAssets(item) {
      return this.assetsFor(item?.code)
        || (this.isFootballTheme(item) ? this.assetsFor('FOOTBALL') : null);
    },
    previewCharacterFor(code) {
      // Names must match the person pictured in each category's char_mock.png
      // (NBA mock is Kobe Bryant, Investors mock is Cathie Wood).
      const samples = {
        WC26:      { name: 'James Rodriguez', firstName: 'James',  lastName: 'Rodriguez',  country: 'Colombia' },
        FOOTBALL:  { name: 'James Rodriguez', firstName: 'James',  lastName: 'Rodriguez',  country: 'Colombia' },
        F1:        { name: 'Lewis Hamilton',  firstName: 'Lewis',  lastName: 'Hamilton',   country: 'United Kingdom' },
        NBA:       { name: 'Kobe Bryant',     firstName: 'Kobe',   lastName: 'Bryant',     country: 'United States' },
        TENNIS:    { name: 'Rafael Nadal',    firstName: 'Rafael', lastName: 'Nadal',      country: 'Spain' },
        INVESTORS: { name: 'Cathie Wood',     firstName: 'Cathie', lastName: 'Wood',       country: 'United States' },
        SINGERS:   { name: 'Bad Bunny',       firstName: 'Bad',    lastName: 'Bunny',      country: 'Puerto Rico' },
        ARTISTS:   { name: 'Bad Bunny',       firstName: 'Bad',    lastName: 'Bunny',      country: 'Puerto Rico' },
      };
      const key = String(code || '').toUpperCase();
      return samples[key] || { name: 'Player 1', firstName: 'Player', lastName: '1', country: null };
    },
    initials(name) {
      return String(name || '').trim().charAt(0).toUpperCase() || '?';
    },
    // Title-case names that arrive all-uppercase and are longer than 3 chars.
    // Preserves short acronyms (F1, NBA) and names with digits (WC26) as-is.
    prettifyName(name) {
      const s = String(name || '').trim();
      if (!s) return s;
      if (s.length <= 3) return s;
      if (/^[A-Z]+$/.test(s)) {
        return s.charAt(0) + s.slice(1).toLowerCase();
      }
      return s;
    },
    async loadThemes() {
      this.loadingThemes = true;
      try {
        const res = await Repository.listSkinThemesForBroker();
        this.themes = Array.isArray(res?.data) ? res.data : [];
      } catch (err) {
        this.themes = [];
      } finally {
        this.loadingThemes = false;
      }
    },
    init() {
      const settings = this.tournamentSummary?.tournamentSettings || {};
      const summary = this.tournamentSummary?.competitionSkinTheme || {};
      this.enabled = !!(settings.skinThemeEnabled ?? summary.enabled);
      this.selectedThemeId = settings.skinTheme?.id ?? summary.themeId ?? null;
      this.initialValues = {
        enabled: this.enabled,
        selectedThemeId: this.selectedThemeId,
      };
      this.checkForChanges();
    },
    checkForChanges() {
      if (!this.initialValues) {
        this.setNextBtnDisabled?.(true);
        return;
      }
      const changed = this.enabled !== this.initialValues.enabled
        || this.selectedThemeId !== this.initialValues.selectedThemeId;
      const valid = !this.enabled || !!this.selectedThemeId;
      this.setNextBtnDisabled?.(!changed || !valid || this.lockedByParticipants);
    },
    async saveTournamentSettings() {
      const summary = this.tournamentSummary;
      if (!summary?.tournamentId) {
        this.$emit('save-error', { message: this.$t('alerts.errorOccurred') });
        return;
      }
      if (this.enabled && !this.selectedThemeId) {
        this.$emit('save-error', { message: this.$t('skinThemeSettings.categoryRequired') });
        return;
      }
      // edit-full-tournament is a full overwrite; re-send current values for everything
      // we're not changing so they don't get nulled.
      const settings = summary.tournamentSettings || {};
      const payload = {
        tournamentId: summary.tournamentId,
        tournamentName: summary.tournamentName,
        startTime: summary.startTime,
        endTime: summary.endTime,
        faqsUrl: settings.faqsUrl ?? summary.faqsUrl ?? null,
        imageUrl: settings.imageUrl ?? summary.imageUrl ?? null,
        termCondition: settings.termsCondition ?? null,
        mainLenguage: settings.mainLanguage ?? null,
        publicPerformance: settings.publicPerformance ?? null,
        inviteOnly: settings.inviteOnly ?? null,
        antiSpoiler: settings.antiSpoiler ?? null,
        daysPostRegister: settings.daysPostRegister ?? summary.daysPostRegister ?? null,
        multiAccount: settings.multiAccount ?? null,
        colors: settings.colors ?? summary.colors ?? null,
        skinThemeEnabled: this.enabled,
        skinThemeId: this.enabled ? this.selectedThemeId : null,
      };
      try {
        const res = await Repository.editFullTournamentSettings(payload);
        if (res.status >= 400) throw new Error('edit-full-tournament failed');
        const overview = await Repository.getTournamentOverview(summary.tournamentId);
        if (overview.status < 400) this.setTournament(overview.data);
        this.initialValues = { enabled: this.enabled, selectedThemeId: this.selectedThemeId };
        this.setNextBtnDisabled?.(true);
        this.$emit('save-success', { message: this.$t('alerts_TournamentSettings.edited') });
      } catch (err) {
        this.$emit('save-error', { message: err.response?.data?.message || this.$t('alerts.errorOccurred') });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.tournament-skin-settings {
  width: 100%;
  font-family: 'Avenir', sans-serif;
}

.section-title-row {
  display: flex;
  align-items: center;
  margin-bottom: 12px;

  .section-title {
    font-weight: 600;
  }
}

.info-card {
  background-color: var(--v-cardBackground-base);
  border: 1px solid #ededed;
  border-radius: 10px;
  padding: 20px;
}
.theme--dark .info-card { border-color: #424242; }

.switch-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.switch-label {
  flex: 1;
}

.switch-title {
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 4px;
}

.switch-desc {
  font-size: 12px;
  color: #9a9a9a;
  line-height: 1.4;
}

.lock-hint {
  margin-top: 10px;
  font-size: 12px;
  color: #9a9a9a;
  display: flex;
  align-items: center;
}

.theme-picker {
  margin-top: 4px;
}

.theme-option {
  display: flex;
  align-items: center;
  gap: 10px;

  .theme-icon-badge {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background-color: #1b1b1b;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .theme-icon {
    width: 18px;
    height: 18px;
    object-fit: contain;
  }

  .theme-icon-fallback {
    color: #f8f8f8;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.5px;
  }
}

.config-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
  gap: 24px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
}

.config-controls {
  min-width: 0;
}

.config-preview {
  min-width: 0;

  .preview-label {
    font-size: 12px;
    font-weight: 600;
    color: #9a9a9a;
    margin-bottom: 8px;
  }

  .preview-frame {
    border: 1px dashed rgba(0, 0, 0, 0.15);
    border-radius: 12px;
    padding: 16px 16px 28px;
    display: flex;
    justify-content: center;
    overflow: hidden;
  }

  .preview-scale {
    width: 290px;
    max-width: 100%;
    pointer-events: none;
    user-select: none;
  }
}

.theme--dark .config-preview .preview-frame {
  border-color: rgba(255, 255, 255, 0.2);
}
</style>
